import { ref, computed } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAcademySelection from '@/composables/useAcademySelection'
import { getAcademyDisplay, getContentPackItemIds } from '@/utils/contentPackAcademyDisplay'

export default function useSessions() {
  const RESOURCE = '/session'

  const { showSnackbar } = useSnackbar()
  const { getContentScopeParams } = useAcademySelection()

  const baseHeaders = [
    { text: 'NAME', value: 'name' },
    { text: 'TYPE', value: 'type', width: 120 },
    { text: 'EXERCISES', value: 'exercises', width: 120 },
    {
      text: 'CREATOR', value: 'creator', sortable: true, width: 150,
    },
    {
      text: 'ACADEMY', value: 'academy', sortable: true, width: 150,
    },
    {
      text: 'ACTIVE', value: 'active', width: 100, class: 'compact-table-column', cellClass: 'compact-table-column',
    },
    {
      text: 'ACTIONS', value: 'actions', sortable: false, width: 100, align: 'center', class: 'compact-table-column', cellClass: 'compact-table-column',
    },
  ]
  const dtHeaders = computed(() => [...baseHeaders])
  const dtSearchQuery = ref('')
  const dtExpanded = ref([])
  const blankSession = {
    id: '',
    uniqueKey: '',
    name: '',
    summary: '',
    level: '',
    category: 'fitness',
    type: '',
    unlockAt: '',
    exercises: [],
  }

  const sessions = ref([])
  const session = ref(structuredClone(blankSession))
  const isLoading = ref(false)

  const createSession = async sessionItem => {
    isLoading.value = true

    await axios
      .post(RESOURCE, sessionItem)
      .then(() => {
        showSnackbar('You have successfully created a new session', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to create a new session:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const deleteSession = async sessionItem => {
    isLoading.value = true
    sessionItem.deleted = true

    await axios
      .put(RESOURCE, sessionItem)
      .then(() => {
        showSnackbar('You have successfully deleted the session', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to delete the session:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const fetchSessions = async category => {
    isLoading.value = true
    const params = { withCreator: true, ...getContentScopeParams() }

    try {
      const [response, contentPacksResponse] = await Promise.all([
        axios.get(RESOURCE, { params }),
        axios.get('/content-pack').catch(() => ({ data: [] })),
      ])
      const contentPackSessionIds = ['fitness', 'practice', 'flow'].reduce((result, sectionKey) => ({
        ...result,
        [sectionKey]: getContentPackItemIds(contentPacksResponse.data, sectionKey, 'sessions'),
      }), {})
      const allSessions = response.data.map(sessionItem => ({
        ...sessionItem,
        academy: getAcademyDisplay(
          sessionItem,
          contentPackSessionIds[String(sessionItem.category || '').toLowerCase()] || new Set(),
        ),
      }))

      if (category) {
        sessions.value = allSessions.filter(s => s.category === category)
      } else {
        sessions.value = allSessions
      }
    } catch (error) {
      showSnackbar('Failed to fetch sessions', SnackbarType.ERROR)
    } finally {
      isLoading.value = false
    }
  }

  const updateSession = async sessionItem => {
    isLoading.value = true

    await axios
      .put(RESOURCE, sessionItem)
      .then(() => {
        showSnackbar('You have successfully updated the session', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to update the session:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const updateManySessions = async (type, paramName, paramValue, ids) => {
    isLoading.value = true

    const payload = {
      type,
      params: [
        {
          name: paramName,
          value: paramValue,
          ids,
        },
      ],
    }

    try {
      await axios.put('/library/updateMany', payload)

      showSnackbar(`Successfully updated ${ids.length} session(s)`, SnackbarType.SUCCESS)

      return true
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to update sessions:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetState = () => {
    sessions.value = []
    session.value = structuredClone(blankSession)
    dtSearchQuery.value = ''
    dtExpanded.value = []
    isLoading.value = false
  }

  return {
    dtHeaders,
    dtSearchQuery,
    dtExpanded,

    blankSession,
    sessions,
    session,

    isLoading,

    createSession,
    deleteSession,
    fetchSessions,
    updateSession,
    updateManySessions,
    resetState,
  }
}
